/**
 * Xano Question Types Service
 * Handles fetching and managing question type configurations
 */

import { xanoClient } from './client';
import type { QuestionType } from '../../shared/types';

interface XanoQuestionType {
  id: number;
  created_at: string;
  updated_at: number;
  name: string;
  code: string;
  description: string;
  allowed_question_formats: any;
  usage_count: number;
  is_active: boolean;
}

// Map question type codes to our internal types
// Based on actual database question types:
// ID 1: Single Choice, ID 2: Multiple Select, ID 3: Fill Blanks
// ID 4: Single Choice (4 opts), ID 5: Single Choice (6 opts)
// ID 6: Clinical Data Table (lab_values), ID 7: Lab Values Format
// ID 8: Image Based, ID 9: Clinical Scenario, ID 10: Management Steps
const QUESTION_TYPE_MAP: Record<string, QuestionType> = {
  'single_choice': 'single_choice',
  'multiple_select': 'multiple_select',
  'fill_blanks': 'fill_blanks',
  'image_based': 'image_based',
  'clinical_scenario': 'clinical_scenario',
  'lab_values': 'lab_values',
  'management_steps': 'management_steps',
  // Legacy mappings
  'MC_BASIC': 'single_choice',
  'MC_CLINICAL': 'clinical_scenario',
  'MULTIPLE_SELECT': 'multiple_select',
  'TRUE_FALSE': 'true_false',
  'true_false': 'true_false',
};

class QuestionTypesService {
  private typesCache: XanoQuestionType[] | null = null;

  /**
   * Get all active question types
   */
  async getQuestionTypes(forceRefresh = false): Promise<XanoQuestionType[]> {
    // Always force refresh in development to ensure we get latest from DB
    const shouldRefresh = forceRefresh || process.env.NODE_ENV === 'development';
    
    if (this.typesCache && !shouldRefresh) {
      return this.typesCache;
    }

    try {
      const response = await xanoClient.get<any>('/question_types');
      
      // Handle both direct array and paginated response (like budgetService)
      const data = Array.isArray(response) ? response : (response.items || response);
      
      if (!data || !Array.isArray(data)) {
        // No question types found in response
        return [];
      }

      // If no question types exist in database, throw error
      if (data.length === 0) {
        throw new Error('No question types configured in database. Please add question types via the admin panel.');
      }
      
      // Cache only active types
      this.typesCache = data.filter(type => type.is_active);
      
      return this.typesCache;
    } catch (error) {
      // Failed to fetch question types
      throw new Error('Failed to load question types');
    }
  }

  /**
   * Get question types for UI selection
   */
  async getQuestionTypeOptions(): Promise<Array<{ value: QuestionType; label: string; description?: string }>> {
    try {
      const types = await this.getQuestionTypes();
      
      // Transform to UI format
      const options = types.map(type => {
        const mappedType = QUESTION_TYPE_MAP[type.code] || 'single_choice';
        return {
          value: mappedType as QuestionType,
          label: type.name,
          description: type.description,
          usageCount: type.usage_count,
          originalCode: type.code // Keep track of original code for debugging
        };
      });

      // Remove duplicates by value, keeping the one with highest usage
      const uniqueMap = new Map<QuestionType, any>();
      options.forEach(option => {
        const existing = uniqueMap.get(option.value);
        if (!existing || option.usageCount > existing.usageCount) {
          uniqueMap.set(option.value, option);
        }
      });

      const uniqueOptions = Array.from(uniqueMap.values());

      // If we only have one type or no types, provide defaults
      if (uniqueOptions.length <= 1) {
        // Limited question types available, providing defaults
        return this.getDefaultQuestionTypes();
      }

      return uniqueOptions.sort((a, b) => b.usageCount - a.usageCount);
    } catch (error) {
      // Failed to get question type options
      // Return default question types as fallback
      return this.getDefaultQuestionTypes();
    }
  }

  /**
   * Get default question types when API fails or returns limited options
   */
  private getDefaultQuestionTypes(): Array<{ value: QuestionType; label: string; description?: string; usageCount?: number }> {
    return [
      {
        value: 'single_choice',
        label: 'Single Choice',
        description: 'Select one correct answer from multiple options',
        usageCount: 100
      },
      {
        value: 'multiple_select',
        label: 'Multiple Select',
        description: 'Select all correct answers that apply',
        usageCount: 50
      },
      {
        value: 'fill_blanks',
        label: 'Fill in the Blanks',
        description: 'Complete the statement with the correct answer',
        usageCount: 40
      },
      {
        value: 'lab_values',
        label: 'Lab Values',
        description: 'Interpret clinical data and lab results',
        usageCount: 35
      },
      {
        value: 'clinical_scenario',
        label: 'Clinical Scenario',
        description: 'Complex patient case with detailed findings',
        usageCount: 30
      },
      {
        value: 'image_based',
        label: 'Image Based',
        description: 'Questions referencing medical imaging',
        usageCount: 25
      },
      {
        value: 'management_steps',
        label: 'Management Steps',
        description: 'Sequence medical interventions correctly',
        usageCount: 20
      }
    ];
  }

  /**
   * Get a specific question type by code
   */
  async getQuestionTypeByCode(code: string): Promise<XanoQuestionType | null> {
    try {
      const types = await this.getQuestionTypes();
      return types.find(type => type.code === code) || null;
    } catch (error) {
      // Failed to get question type by code
      return null;
    }
  }

  /**
   * Map internal question type to Xano code
   */
  async getCodeForQuestionType(questionType: QuestionType): Promise<string> {
    // Reverse lookup
    const entry = Object.entries(QUESTION_TYPE_MAP).find(([_, value]) => value === questionType);
    if (entry) {
      return entry[0];
    }

    // Try to find a matching type by name
    const types = await this.getQuestionTypes();
    const matchingType = types.find(type => 
      type.name.toLowerCase().includes(questionType.replace('_', ' '))
    );

    return matchingType?.code || 'MC_BASIC';
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.typesCache = null;
  }
}

export const questionTypesService = new QuestionTypesService();